import { useEffect } from 'react';

const SITE_NAME = 'Breck Vipers';
const SITE_URL = 'https://breckvipers.com';
const DEFAULT_TITLE = 'Breck Vipers | Official Mountain Hockey League Team | Breckenridge, Colorado';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

export interface PageMetaOptions {
  /** Canonical path (defaults to the current path, without query or trailing slash). */
  path?: string;
  /** Absolute share image for Open Graph / Twitter / AI previews. */
  image?: string;
  /** Open Graph type, e.g. "article" for news. */
  type?: string;
  /** Keep this page out of search results (404s, checkout, etc.). */
  noindex?: boolean;
}

// Find-or-create a <meta>/<link> so every page can set it, and remember the
// previous value so the cleanup restores it on navigation.
function upsert(selector: string, create: () => HTMLElement, attr: string, value: string): () => void {
  let el = document.head.querySelector<HTMLElement>(selector);
  let created = false;
  if (!el) {
    el = create();
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => {
    if (created) el?.remove();
    else if (prev !== null) el?.setAttribute(attr, prev);
  };
}
const meta = (key: 'name' | 'property', name: string, content: string) =>
  upsert(`meta[${key}="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(key, name);
    return m;
  }, 'content', content);

export function usePageMeta(title?: string, description?: string, options: PageMetaOptions = {}) {
  const { path, image, type, noindex } = options;
  useEffect(() => {
    const prevTitle = document.title;
    const fullTitle = title
      ? title === DEFAULT_TITLE
        ? title
        : `${title} | ${SITE_NAME}`
      : DEFAULT_TITLE;
    document.title = fullTitle;

    const rawPath = path ?? window.location.pathname;
    const cleanPath = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : '/';
    const url = `${SITE_URL}${cleanPath}`;

    const restores: Array<() => void> = [
      upsert('link[rel="canonical"]', () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      }, 'href', url),
      meta('property', 'og:url', url),
      meta('name', 'twitter:url', url),
      meta('property', 'og:title', fullTitle),
      meta('name', 'twitter:title', fullTitle),
    ];
    if (description) {
      restores.push(
        meta('name', 'description', description),
        meta('property', 'og:description', description),
        meta('name', 'twitter:description', description)
      );
    }
    restores.push(
      meta('property', 'og:image', image || DEFAULT_IMAGE),
      meta('name', 'twitter:image', image || DEFAULT_IMAGE),
      meta('property', 'og:type', type || 'website')
    );
    if (noindex) restores.push(meta('name', 'robots', 'noindex, follow'));

    return () => {
      document.title = prevTitle;
      restores.reverse().forEach((r) => r());
    };
  }, [title, description, path, image, type, noindex]);
}
